import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatErr } from "../lib/api";

const CATEGORIES = ["GUIDE", "TOOLKIT", "COURSE", "TEMPLATE"];

function ProductForm({ initial, onSaved, onCancel }) {
  const isEdit = Boolean(initial?.id);
  const [form, setForm] = useState({
    title: initial?.title || "",
    slug: initial?.slug || "",
    category: initial?.category || "GUIDE",
    short_description: initial?.short_description || "",
    long_description: initial?.long_description || "",
    price: initial?.price ?? "",
    external_url: initial?.external_url || "",
    video_url: initial?.video_url || "",
    thumbnail_url: initial?.thumbnail_url || "",
  });
  const [thumbnail, setThumbnail] = useState(null);
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (thumbnail) fd.append("thumbnail", thumbnail);
    if (file) fd.append("file", file);
    try {
      if (isEdit) await api.put(`/admin/products/${initial.id}`, fd);
      else await api.post("/admin/products", fd);
      onSaved();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="opus-card p-8 space-y-5" data-testid="product-form">
      <h3 className="font-serif text-3xl" style={{ fontWeight: 500 }}>{isEdit ? "Edit resource" : "Add a new resource"}</h3>

      <div>
        <label className="text-xs uppercase tracking-[0.2em] block mb-2">Title</label>
        <input data-testid="form-title" value={form.title} onChange={update("title")} required className="opus-input" />
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.2em] block mb-2">URL slug</label>
        <input
          data-testid="form-slug"
          value={form.slug}
          onChange={update("slug")}
          className="opus-input"
          placeholder="e.g. pipeline-template"
        />
        <p className="text-xs mt-1" style={{ color: "var(--opus-text-muted)" }}>
          Used in the product page URL (e.g. /product/<strong>{form.slug || "pipeline-template"}</strong>). Leave blank to auto-generate from the title. Lowercase letters, numbers, and dashes only.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] block mb-2">Category</label>
          <select data-testid="form-category" value={form.category} onChange={update("category")} className="opus-input">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] block mb-2">Price (USD)</label>
          <input data-testid="form-price" type="number" step="0.01" min="0" value={form.price} onChange={update("price")} required className="opus-input" />
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.2em] block mb-2">Short description</label>
        <input data-testid="form-short" value={form.short_description} onChange={update("short_description")} required className="opus-input" />
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.2em] block mb-2">Long description</label>
        <textarea data-testid="form-long" value={form.long_description} onChange={update("long_description")} rows={4} className="opus-input" />
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.2em] block mb-2">Squarespace product URL (Buy Now link)</label>
        <input data-testid="form-external-url" value={form.external_url} onChange={update("external_url")} type="url" className="opus-input" placeholder="https://www.opusbusinessoperations.com/shop/your-product" />
        <p className="text-xs mt-1" style={{ color: "var(--opus-text-muted)" }}>
          Customers will be sent here when they click Buy Now. Leave blank if not yet available.
        </p>
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.2em] block mb-2">Preview video URL (MP4)</label>
        <input data-testid="form-video-url" value={form.video_url} onChange={update("video_url")} type="url" className="opus-input" placeholder="https://… .mp4" />
        <p className="text-xs mt-1" style={{ color: "var(--opus-text-muted)" }}>
          Optional. MP4 plays in all browsers (.mov does not).
        </p>
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.2em] block mb-2">Thumbnail URL (optional)</label>
        <input data-testid="form-thumb-url" value={form.thumbnail_url} onChange={update("thumbnail_url")} className="opus-input" placeholder="https://… (or upload below)" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] block mb-2">Thumbnail upload</label>
          <input data-testid="form-thumb-file" type="file" accept="image/*" onChange={(e) => setThumbnail(e.target.files?.[0] || null)} className="opus-input" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] block mb-2">Digital file (PDF/ZIP)</label>
          <input data-testid="form-file" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="opus-input" />
        </div>
      </div>

      {error ? <p className="text-sm" style={{ color: "#A0392C" }}>{error}</p> : null}

      <div className="flex gap-3">
        <button type="submit" disabled={saving} data-testid="form-save" className="btn-sage px-6 py-3 text-xs tracking-[0.22em] uppercase disabled:opacity-50" style={{ borderRadius: "2px" }}>
          {saving ? "Saving…" : isEdit ? "Save changes" : "Create resource"}
        </button>
        <button type="button" onClick={onCancel} data-testid="form-cancel" className="px-6 py-3 text-xs tracking-[0.22em] uppercase border" style={{ borderRadius: "2px", borderColor: "var(--opus-border)", color: "var(--opus-text-secondary)" }}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [promos, setPromos] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsWindow, setAnalyticsWindow] = useState(30);
  const [editing, setEditing] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(true);

  // Promo form state
  const [newPromo, setNewPromo] = useState({ code: "", discount_percent: 100, product_id: "", usage_limit: "", expires_at: "" });
  const [promoFormError, setPromoFormError] = useState("");
  const [promoSaving, setPromoSaving] = useState(false);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) nav("/admin/login");
  }, [user, loading, nav]);

  const refresh = async () => {
    setLoadingProducts(true);
    try {
      const [p, o, pr] = await Promise.all([
        api.get("/admin/products"),
        api.get("/admin/orders"),
        api.get("/admin/promo-codes"),
      ]);
      setProducts(p.data); setOrders(o.data); setPromos(pr.data);
    } finally { setLoadingProducts(false); }
  };

  useEffect(() => { if (user && user.role === "admin") refresh(); }, [user]);

  useEffect(() => {
    if (tab === "analytics" && user && user.role === "admin") {
      api.get(`/admin/analytics?days=${analyticsWindow}`).then((r) => setAnalytics(r.data)).catch(() => {});
    }
  }, [tab, analyticsWindow, user]);

  const remove = async (id) => {
    if (!window.confirm("Delete this resource permanently?")) return;
    await api.delete(`/admin/products/${id}`);
    refresh();
  };

  const createPromo = async (e) => {
    e.preventDefault();
    setPromoFormError(""); setPromoSaving(true);
    try {
      const payload = {
        code: newPromo.code.trim().toUpperCase(),
        discount_percent: Number(newPromo.discount_percent),
        product_id: newPromo.product_id || null,
        usage_limit: newPromo.usage_limit ? Number(newPromo.usage_limit) : null,
        expires_at: newPromo.expires_at || null,
        is_active: true,
      };
      await api.post("/admin/promo-codes", payload);
      setNewPromo({ code: "", discount_percent: 100, product_id: "", usage_limit: "", expires_at: "" });
      refresh();
    } catch (e) {
      setPromoFormError(e.response?.data?.detail || e.message || "Could not create code");
    } finally { setPromoSaving(false); }
  };

  const deletePromo = async (id) => {
    if (!window.confirm("Delete this code?")) return;
    await api.delete(`/admin/promo-codes/${id}`);
    refresh();
  };

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center text-xs tracking-widest uppercase">Loading…</div>;
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--opus-bg)" }}>
      <header className="border-b" style={{ borderColor: "var(--opus-border)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div>
            <p className="text-xs tracking-[0.28em] uppercase" style={{ color: "var(--opus-sage)" }}>Admin</p>
            <p className="font-serif text-xl mt-1" data-testid="admin-header">Opus Resources Dashboard</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs uppercase tracking-widest" style={{ color: "var(--opus-text-muted)" }}>{user.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="text-xs tracking-[0.2em] uppercase underline" style={{ color: "var(--opus-sage)" }}>
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="flex items-center gap-3 mb-8 flex-wrap">
          <button onClick={() => setTab("products")} data-testid="tab-products" className={`filter-pill ${tab === "products" ? "active" : ""}`}>Products ({products.length})</button>
          <button onClick={() => setTab("orders")} data-testid="tab-orders" className={`filter-pill ${tab === "orders" ? "active" : ""}`}>Orders ({orders.length})</button>
          <button onClick={() => setTab("promos")} data-testid="tab-promos" className={`filter-pill ${tab === "promos" ? "active" : ""}`}>Promo codes ({promos.length})</button>
          <button onClick={() => setTab("analytics")} data-testid="tab-analytics" className={`filter-pill ${tab === "analytics" ? "active" : ""}`}>Analytics</button>
        </div>

        {tab === "products" && (
          <div className="space-y-6">
            {editing ? (
              <ProductForm
                initial={editing === "new" ? null : editing}
                onSaved={() => { setEditing(null); refresh(); }}
                onCancel={() => setEditing(null)}
              />
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-3xl" style={{ fontWeight: 500 }}>Resources</h2>
                  <button onClick={() => setEditing("new")} data-testid="add-product" className="btn-sage px-5 py-2.5 text-xs tracking-[0.22em] uppercase" style={{ borderRadius: "2px" }}>+ Add resource</button>
                </div>

                {loadingProducts ? (
                  <p className="text-xs uppercase tracking-widest" style={{ color: "var(--opus-text-muted)" }}>Loading…</p>
                ) : products.length === 0 ? (
                  <p className="text-sm" style={{ color: "var(--opus-text-muted)" }}>No products yet.</p>
                ) : (
                  <div className="opus-card overflow-hidden">
                    <table className="w-full text-sm" data-testid="products-table">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-widest" style={{ background: "var(--opus-bg-secondary)", color: "var(--opus-text-secondary)" }}>
                          <th className="p-4">Title</th>
                          <th className="p-4">Category</th>
                          <th className="p-4">Price</th>
                          <th className="p-4">File</th>
                          <th className="p-4">Status</th>
                          <th className="p-4"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.map((p) => (
                          <tr key={p.id} className="border-t" style={{ borderColor: "var(--opus-border)" }} data-testid={`row-product-${p.id}`}>
                            <td className="p-4 font-serif text-base" style={{ color: "var(--opus-text)" }}>{p.title}</td>
                            <td className="p-4 text-xs uppercase tracking-widest" style={{ color: "var(--opus-sage)" }}>{p.category}</td>
                            <td className="p-4">${Number(p.price).toFixed(2)}</td>
                            <td className="p-4">{p.has_file ? "✓ uploaded" : <span style={{ color: "var(--opus-text-muted)" }}>placeholder</span>}</td>
                            <td className="p-4">{p.is_active ? "Active" : "Hidden"}</td>
                            <td className="p-4 whitespace-nowrap">
                              <button onClick={() => setEditing(p)} data-testid={`edit-${p.id}`} className="text-xs tracking-widest uppercase underline mr-4" style={{ color: "var(--opus-sage)" }}>Edit</button>
                              <button onClick={() => remove(p.id)} data-testid={`delete-${p.id}`} className="text-xs tracking-widest uppercase underline" style={{ color: "#A0392C" }}>Delete</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {tab === "orders" && (
          <>
            <h2 className="font-serif text-3xl mb-6" style={{ fontWeight: 500 }}>Orders</h2>
            {orders.length === 0 ? (
              <p className="text-sm" style={{ color: "var(--opus-text-muted)" }}>No orders yet.</p>
            ) : (
              <div className="opus-card overflow-hidden">
                <table className="w-full text-sm" data-testid="orders-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-widest" style={{ background: "var(--opus-bg-secondary)", color: "var(--opus-text-secondary)" }}>
                      <th className="p-4">Date</th>
                      <th className="p-4">Customer</th>
                      <th className="p-4">Product</th>
                      <th className="p-4">Amount</th>
                      <th className="p-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => (
                      <tr key={o.id} className="border-t" style={{ borderColor: "var(--opus-border)" }}>
                        <td className="p-4">{new Date(o.created_at).toLocaleString()}</td>
                        <td className="p-4">{o.customer_email}</td>
                        <td className="p-4 font-serif">{o.product_title}</td>
                        <td className="p-4">${Number(o.amount).toFixed(2)}</td>
                        <td className="p-4 text-xs uppercase tracking-widest" style={{ color: "var(--opus-sage)" }}>{o.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {tab === "promos" && (
          <>
            <h2 className="font-serif text-3xl mb-6" style={{ fontWeight: 500 }}>Promo codes</h2>

            <form onSubmit={createPromo} className="opus-card p-7 mb-8" data-testid="promo-create-form">
              <h3 className="font-serif text-xl mb-4" style={{ fontWeight: 500 }}>Create a new code</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] block mb-2">Code</label>
                  <input
                    required
                    value={newPromo.code}
                    onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                    placeholder="FRIEND100"
                    className="opus-input"
                    style={{ textTransform: "uppercase", letterSpacing: "0.12em" }}
                    data-testid="promo-new-code"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] block mb-2">Discount %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    required
                    value={newPromo.discount_percent}
                    onChange={(e) => setNewPromo({ ...newPromo, discount_percent: e.target.value })}
                    className="opus-input"
                    data-testid="promo-new-percent"
                  />
                  <p className="text-xs mt-1" style={{ color: "var(--opus-text-muted)" }}>100 = free</p>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] block mb-2">Applies to</label>
                  <select
                    value={newPromo.product_id}
                    onChange={(e) => setNewPromo({ ...newPromo, product_id: e.target.value })}
                    className="opus-input"
                    data-testid="promo-new-product"
                  >
                    <option value="">All products</option>
                    {products.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] block mb-2">Usage limit (optional)</label>
                  <input
                    type="number"
                    min="1"
                    value={newPromo.usage_limit}
                    onChange={(e) => setNewPromo({ ...newPromo, usage_limit: e.target.value })}
                    className="opus-input"
                    placeholder="Unlimited"
                    data-testid="promo-new-limit"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs uppercase tracking-[0.2em] block mb-2">Expires (optional)</label>
                  <input
                    type="date"
                    value={newPromo.expires_at}
                    onChange={(e) => setNewPromo({ ...newPromo, expires_at: e.target.value })}
                    className="opus-input"
                    data-testid="promo-new-expires"
                  />
                </div>
              </div>
              {promoFormError ? <p className="text-sm mt-3" style={{ color: "#A0392C" }}>{promoFormError}</p> : null}
              <button
                type="submit"
                disabled={promoSaving}
                className="btn-sage mt-5 px-6 py-3 text-xs tracking-[0.22em] uppercase disabled:opacity-50"
                style={{ borderRadius: "2px" }}
                data-testid="promo-create-submit"
              >
                {promoSaving ? "Saving…" : "Create code"}
              </button>
            </form>

            {promos.length === 0 ? (
              <p className="text-sm" style={{ color: "var(--opus-text-muted)" }}>No promo codes yet.</p>
            ) : (
              <div className="opus-card overflow-hidden">
                <table className="w-full text-sm" data-testid="promos-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-widest" style={{ background: "var(--opus-bg-secondary)", color: "var(--opus-text-secondary)" }}>
                      <th className="p-4">Code</th>
                      <th className="p-4">Discount</th>
                      <th className="p-4">Applies to</th>
                      <th className="p-4">Used</th>
                      <th className="p-4">Expires</th>
                      <th className="p-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {promos.map((pc) => {
                      const productTitle = pc.product_id
                        ? (products.find((p) => p.id === pc.product_id)?.title || "Specific product")
                        : "All products";
                      return (
                        <tr key={pc.id} className="border-t" style={{ borderColor: "var(--opus-border)" }} data-testid={`row-promo-${pc.code}`}>
                          <td className="p-4 font-serif tracking-widest">{pc.code}</td>
                          <td className="p-4">{pc.discount_percent}%{pc.discount_percent === 100 ? " (free)" : ""}</td>
                          <td className="p-4">{productTitle}</td>
                          <td className="p-4">{pc.used_count}{pc.usage_limit ? ` / ${pc.usage_limit}` : ""}</td>
                          <td className="p-4">{pc.expires_at ? new Date(pc.expires_at).toLocaleDateString() : "—"}</td>
                          <td className="p-4">
                            <button onClick={() => deletePromo(pc.id)} className="text-xs tracking-widest uppercase underline" style={{ color: "#A0392C" }}>Delete</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {tab === "analytics" && (
          <>
            <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
              <h2 className="font-serif text-3xl" style={{ fontWeight: 500 }}>Analytics</h2>
              <div className="flex items-center gap-2" data-testid="analytics-range">
                {[7, 30, 90].map((d) => (
                  <button
                    key={d}
                    onClick={() => setAnalyticsWindow(d)}
                    className={`filter-pill ${analyticsWindow === d ? "active" : ""}`}
                    data-testid={`analytics-range-${d}`}
                  >
                    Last {d} days
                  </button>
                ))}
              </div>
            </div>

            {!analytics ? (
              <p className="text-xs uppercase tracking-widest" style={{ color: "var(--opus-text-muted)" }}>Loading…</p>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="analytics-cards">
                  {[
                    { label: "Page views", value: analytics.summary.total_page_views },
                    { label: "Unique visitors", value: analytics.summary.unique_visitors },
                    { label: "Product views", value: analytics.summary.total_product_views },
                    { label: "Click-throughs", value: analytics.summary.total_external_clicks, sub: `${analytics.summary.ctr_percent}% CTR` },
                  ].map((c) => (
                    <div key={c.label} className="opus-card p-6">
                      <p className="text-[0.7rem] tracking-[0.28em] uppercase" style={{ color: "var(--opus-text-muted)" }}>{c.label}</p>
                      <p className="font-serif text-4xl mt-2" style={{ color: "var(--opus-text)", fontWeight: 500 }}>{c.value}</p>
                      {c.sub ? <p className="text-xs mt-1" style={{ color: "var(--opus-sage)" }}>{c.sub}</p> : null}
                    </div>
                  ))}
                </div>

                <h3 className="font-serif text-2xl mb-4" style={{ fontWeight: 500 }}>Per product</h3>
                {analytics.products.length === 0 ? (
                  <p className="text-sm mb-10" style={{ color: "var(--opus-text-muted)" }}>No product activity in this window yet.</p>
                ) : (
                  <div className="opus-card overflow-hidden mb-10">
                    <table className="w-full text-sm" data-testid="analytics-products-table">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-widest" style={{ background: "var(--opus-bg-secondary)", color: "var(--opus-text-secondary)" }}>
                          <th className="p-4">Product</th>
                          <th className="p-4">Views</th>
                          <th className="p-4">Unique visitors</th>
                          <th className="p-4">Click-throughs</th>
                          <th className="p-4">CTR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.products.map((p) => (
                          <tr key={p.product_id} className="border-t" style={{ borderColor: "var(--opus-border)" }}>
                            <td className="p-4 font-serif text-base" style={{ color: "var(--opus-text)" }}>{p.title}</td>
                            <td className="p-4">{p.views}</td>
                            <td className="p-4">{p.unique_sessions}</td>
                            <td className="p-4">{p.clicks}{!p.has_external_url ? <span className="text-xs ml-2" style={{ color: "var(--opus-text-muted)" }}>(no URL)</span> : null}</td>
                            <td className="p-4">{p.ctr}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <h3 className="font-serif text-2xl mb-4" style={{ fontWeight: 500 }}>Daily activity</h3>
                {analytics.daily.length === 0 ? (
                  <p className="text-sm mb-10" style={{ color: "var(--opus-text-muted)" }}>No activity yet.</p>
                ) : (
                  <div className="opus-card p-6 mb-10" data-testid="analytics-daily-chart">
                    {(() => {
                      const max = Math.max(1, ...analytics.daily.map((d) => d.page_views));
                      return (
                        <div className="space-y-2">
                          {analytics.daily.slice(-30).map((d) => (
                            <div key={d.date} className="flex items-center gap-3 text-xs">
                              <span className="font-body w-24 shrink-0" style={{ color: "var(--opus-text-muted)" }}>{d.date}</span>
                              <div className="flex-1 h-5 relative" style={{ background: "var(--opus-bg-secondary)", borderRadius: "2px" }}>
                                <div style={{ background: "var(--opus-sage)", height: "100%", width: `${(d.page_views / max) * 100}%`, borderRadius: "2px" }} />
                              </div>
                              <span className="w-12 text-right font-body" style={{ color: "var(--opus-text)" }}>{d.page_views}</span>
                              <span className="w-16 text-right font-body" style={{ color: "var(--opus-sage)" }}>{d.external_clicks} clicks</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}

                <h3 className="font-serif text-2xl mb-4" style={{ fontWeight: 500 }}>Recent events</h3>
                {analytics.recent_events.length === 0 ? (
                  <p className="text-sm" style={{ color: "var(--opus-text-muted)" }}>No events yet.</p>
                ) : (
                  <div className="opus-card overflow-hidden">
                    <table className="w-full text-sm" data-testid="analytics-recent-table">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-widest" style={{ background: "var(--opus-bg-secondary)", color: "var(--opus-text-secondary)" }}>
                          <th className="p-4">Time</th>
                          <th className="p-4">Event</th>
                          <th className="p-4">Page / Product</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.recent_events.map((e) => (
                          <tr key={e.id} className="border-t" style={{ borderColor: "var(--opus-border)" }}>
                            <td className="p-4 text-xs" style={{ color: "var(--opus-text-muted)" }}>{new Date(e.created_at).toLocaleString()}</td>
                            <td className="p-4 text-xs uppercase tracking-widest" style={{ color: "var(--opus-sage)" }}>{e.event_type.replace("_", " ")}</td>
                            <td className="p-4 text-xs">{e.product_title || e.path || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
